import { Box, Stack, Typography, TextField, Button, Link } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export type States = "success" | "error";
import useFormValidity from "./stepUtils";

interface ContentITem {
    gap: number;
    title: React.JSX.Element;
    messages: React.JSX.Element[];
    action?: React.JSX.Element;
    warning?: React.JSX.Element;
}

interface ReturnRegisterProps {
    stateData: States;
    emailProvided: string;
    handleBack: () => void;
}

function ReturnRegister({
                            stateData,
                            emailProvided,
                            handleBack,
                        }: ReturnRegisterProps): React.JSX.Element {
    const { t } = useTranslation();
    const { control } = useFormContext();
    const isFormsValid = useFormValidity(["email", "confirm_email"]);
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(10);

    useEffect(() => {
        if (stateData !== "success") return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    void navigate({ to: "/login" });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [stateData, navigate]);

    const content: Record<States, ContentITem> = {
        success: {
            gap: 3,
            title: (
                <>
                    <Typography color="success.main" variant="h4">
                        {t("register.returnSuccess.title")}
                    </Typography>
                    <Typography
                        style={{ fontWeight: "300", marginTop: "-22px" }}
                        variant="subtitle1"
                    >
                        {t("register.returnSuccess.almostThere")}
                    </Typography>
                </>
            ),
            messages: [
                <Typography key="1">
                    {t("register.returnSuccess.emailSent")}
                </Typography>,
            ],
            action: (
                <TextField
                    color="primary"
                    disabled
                    error={false}
                    fullWidth
                    helperText=""
                    label={t("register.returnSuccess.emailLabel")}
                    sx={{
                        "& .MuiFormLabel-root": {
                            color: "primary.main",
                        },
                        "& .MuiInputBase-input": {
                            color: "primary.main",
                        },
                    }}
                    value={emailProvided}
                    variant="outlined"
                />
            ),
            warning: (
                <Box>
                    <Typography>
                        {t("register.returnSuccess.validateAccess")}
                    </Typography>
                    <Typography color="text.secondary" sx={{ marginTop: "1rem", fontSize: "0.8rem" }}>
                        {t("register.returnSuccess.redirecting", { countdown })}{" "}
                        <Link
                            component="button"
                            onClick={() => {
                                void navigate({ to: "/login" });
                            }}
                            sx={{ verticalAlign: "baseline", fontWeight: "bold" }}
                            underline="hover"
                        >
                            {t("register.returnSuccess.clickHere")}
                        </Link>
                    </Typography>
                </Box>
            ),
        },
        error: {
            gap: 3,
            title: (
                <div>
                    <Typography color="red" variant="h4">
                        {t("register.returnError.title")}
                    </Typography>
                    <Typography
                        style={{ marginTop: "1rem", marginBottom: "2rem" }}
                        variant="body2"
                    >
                        {t("register.returnError.message")}
                    </Typography>
                    <TextField
                        color="primary"
                        disabled
                        error={false}
                        fullWidth
                        helperText=""
                        label={t("register.returnError.emailLabel")}
                        sx={{
                            "& .MuiFormLabel-root": {
                                color: "primary.main",
                            },
                            "& .MuiInputBase-input": {
                                color: "primary.main",
                            },
                        }}
                        value={emailProvided}
                        variant="outlined"
                    />
                </div>
            ),
            messages: [
                <Typography key="1" style={{ marginTop: "1.5rem" }}
                    dangerouslySetInnerHTML={{ __html: t("register.returnError.chooseAnother") }}
                />,
            ],
            action: (
                <>
                    <Controller
                        control={control}
                        name="email"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                color="primary"
                                error={Boolean(error)}
                                fullWidth
                                helperText={error?.message || " "}
                                label={t("register.returnError.emailFieldLabel")}
                                sx={{
                                    "& .MuiFormLabel-root": {
                                        color: "primary.main",
                                    },
                                    "& .MuiInputBase-input": {
                                        color: "primary.main",
                                    },
                                }}
                                variant="outlined"
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="confirm_email"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                color="primary"
                                error={Boolean(error)}
                                fullWidth
                                helperText={error?.message || " "}
                                label={t("register.returnError.confirmEmailLabel")}
                                sx={{
                                    "& .MuiFormLabel-root": {
                                        color: "primary.main",
                                    },
                                    "& .MuiInputBase-input": {
                                        color: "primary.main",
                                    },
                                }}
                                variant="outlined"
                            />
                        )}
                    />
                </>
            ),
            warning: (
                <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="space-between"
                    marginTop="5%"
                    width="100%"
                >
                    <Button
                        color="secondary"
                        onClick={handleBack}
                        size="large"
                        tabIndex={-1}
                        type="button"
                        variant="outlined"
                    >
                        {t("common.previous")}
                    </Button>
                    <Button
                        color="primary"
                        disabled={!isFormsValid}
                        size="large"
                        tabIndex={0}
                        type="submit"
                        variant="contained"
                    >
                        {t("common.finish")}
                    </Button>
                </Box>
            ),
        },
    };

    const selectedContent = content[stateData];

    return (
        <section>
            <Stack gap={selectedContent.gap}>
                {selectedContent.title}
                {selectedContent.messages}
                {selectedContent.action}
                {selectedContent.warning}
            </Stack>
        </section>
    );
}

export default ReturnRegister;
